/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
import React, { useState, useCallback, useEffect } from "react";
import { loadWeb3, getWeb3Instance } from "./Connectivity/Connectivity";
import { toast } from "react-toastify";
import {
  tradeXNftMarketplace,
  tradeXNftMarketplaceAbi,
  USDT,
  USDTAbi,
} from "./Contract";
import "bootstrap/dist/css/bootstrap.min.css";

export default function MarketplacePage() {
  const [btnTxt, setBtnTxt] = useState("Connect Wallet");
  const [account, setAccount] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [loadingStates, setLoadingStates] = useState({});
  
  // Function inputs and outputs storage
  const [functionInputs, setFunctionInputs] = useState({});
  const [functionOutputs, setFunctionOutputs] = useState({});
  
  // Overview data
  const [marketplaceData, setMarketplaceData] = useState({
    totalNftsMinted: "",
    maxNftSupply: "",
    nftBasePrice: "",
    queueLength: "",
    isClaimEnabled: false,
    usdtBalance: "",
    userUsdtBalance: ""
  });

  // Marketplace contract functions definitions
  const marketplaceFunctions = {
    // Read Functions (View Functions)
    readFunctions: [
      // Basic Information Functions
      {
        name: "adminWallet",
        title: "Admin Wallet",
        description: "Get the administrative wallet address",
        inputs: [],
        icon: "👑",
        category: "Basic Info"
      },
      {
        name: "tradexToken",
        title: "Tradex Token",
        description: "Get the Tradex token address",
        inputs: [],
        icon: "🪐",
        category: "Basic Info"
      },
      {
        name: "autoTradeFee",
        title: "Auto Trade Fee",
        description: "Get auto trade fee percentage",
        inputs: [],
        icon: "🤖",
        category: "Trading"
      },
      {
        name: "autoTradeFeeReceiver",
        title: "Auto Trade Fee Receiver",
        description: "Get auto trade fee receiver wallet",
        inputs: [],
        icon: "📥",
        category: "Basic Info"
      },
      {
        name: "botTimeDuration",
        title: "Bot Time Duration",
        description: "Get bot time duration",
        inputs: [],
        icon: "⏱️",
        category: "Stats"
      },
      {
        name: "buyCount",
        title: "Buy Count",
        description: "Get total buy count",
        inputs: [],
        icon: "🛒",
        category: "Stats"
      },
      {
        name: "isAutoTradeFee",
        title: "Is Auto Trade Fee",
        description: "Check if auto trade fee is enabled",
        inputs: [],
        icon: "✅",
        category: "Trading",
        formatOutput: (value) => value ? 'Enabled' : 'Disabled'
      },
      {
        name: "buringIncomePercent",
        title: "Burning Income %",
        description: "Get the burning income percentage",
        inputs: [],
        icon: "🔥",
        category: "Income",
        formatOutput: (value) => `${value}%`
      },
      {
        name: "globalPeriod",
        title: "Global Period",
        description: "Get the global period",
        inputs: [],
        icon: "⏱️",
        category: "Stats"
      },
      {
        name: "isClaimEnable",
        title: "Claim Status",
        description: "Check if claiming is enabled",
        inputs: [],
        icon: "✅",
        category: "Basic Info",
        formatOutput: (value) => value ? 'Enabled' : 'Disabled'
      },
      {
        name: "levelIncomePercent",
        title: "Level Income %",
        description: "Get the level income percentage",
        inputs: [],
        icon: "📊",
        category: "Income",
        formatOutput: (value) => `${value}%`
      },
      {
        name: "lpIncomePercent",
        title: "LP Income %",
        description: "Get the LP income percentage",
        inputs: [],
        icon: "💧",
        category: "Income",
        formatOutput: (value) => `${value}%`
      },
      {
        name: "maxNftSupply",
        title: "Max NFT Supply",
        description: "Get the maximum NFT supply",
        inputs: [],
        icon: "📈",
        category: "NFT Config"
      },
      {
        name: "maxSalePrice",
        title: "Max Sale Price",
        description: "Get the maximum sale price",
        inputs: [],
        icon: "💲",
        category: "Trading"
      },
      {
        name: "mintNftCount",
        title: "Mint NFT Count",
        description: "Get the mint NFT count",
        inputs: [],
        icon: "🔢",
        category: "NFT Config"
      },
      {
        name: "nftBasePrice",
        title: "NFT Base Price",
        description: "Get the NFT base price",
        inputs: [],
        icon: "💵",
        category: "NFT Config"
      },
      {
        name: "nftBurnBasePrice",
        title: "NFT Burn Base Price",
        description: "Get the NFT burn base price",
        inputs: [],
        icon: "🔥",
        category: "NFT Config"
      },
      {
        name: "nftContract",
        title: "NFT Contract",
        description: "Get the NFT contract address",
        inputs: [],
        icon: "🎨",
        category: "Basic Info"
      },
      {
        name: "nftDeadDuration",
        title: "NFT Dead Duration",
        description: "Get the NFT dead duration",
        inputs: [],
        icon: "⏳",
        category: "NFT Config"
      },
      {
        name: "nftMatrixContract",
        title: "NFT Matrix Contract",
        description: "Get the NFT matrix contract address",
        inputs: [],
        icon: "🤖",
        category: "Basic Info"
      },
      {
        name: "nftOwner",
        title: "NFT Owner",
        description: "Get owner of specific NFT",
        inputs: [
          { name: "tokenId", type: "uint256", placeholder: "Enter token ID", required: true }
        ],
        icon: "👤",
        category: "Trading"
      },
      {
        name: "nftOwnerIncomePercent",
        title: "NFT Owner Income %",
        description: "Get the NFT owner income percentage",
        inputs: [],
        icon: "💰",
        category: "Income",
        formatOutput: (value) => `${value}%`
      },
      {
        name: "nftQueueStart",
        title: "NFT Queue Start",
        description: "Get the NFT queue start",
        inputs: [],
        icon: "⬆️",
        category: "Queue Management"
      },
      {
        name: "nftQueueIsEmpty",
        title: "NFT Queue Is Empty",
        description: "Check if NFT queue is empty",
        inputs: [],
        icon: "❓",
        category: "Queue Management",
        formatOutput: (value) => value ? 'Yes' : 'No'
      },
      {
        name: "nftQueueLength",
        title: "NFT Queue Length",
        description: "Get the NFT queue length",
        inputs: [],
        icon: "📏",
        category: "Queue Management"
      },
      {
        name: "nftQueueEnd",
        title: "NFT Queue End",
        description: "Get the NFT queue end",
        inputs: [],
        icon: "⬇️",
        category: "Queue Management"
      },
      {
        name: "nftReward",
        title: "NFT Reward",
        description: "Get NFT reward contract address",
        inputs: [],
        icon: "🏆",
        category: "Basic Info"
      },
      {
        name: "owner",
        title: "Owner",
        description: "Get the contract owner",
        inputs: [],
        icon: "👑",
        category: "Basic Info"
      },
      {
        name: "pair",
        title: "Pair",
        description: "Get the Uniswap pair address",
        inputs: [],
        icon: "🔗",
        category: "Basic Info"
      },
      {
        name: "paused",
        title: "Paused",
        description: "Check if contract is paused",
        inputs: [],
        icon: "⏸️",
        category: "Basic Info",
        formatOutput: (value) => value ? 'Yes' : 'No'
      },
      {
        name: "platformIncomePercent",
        title: "Platform Income %",
        description: "Get the platform income percentage",
        inputs: [],
        icon: "🏢",
        category: "Income",
        formatOutput: (value) => `${value}%`
      },
      {
        name: "platformRewardReceiver",
        title: "Platform Reward Receiver",
        description: "Get the platform reward receiver address",
        inputs: [],
        icon: "📤",
        category: "Basic Info"
      },
      {
        name: "queueRequire",
        title: "Queue Require",
        description: "Get the queue requirement",
        inputs: [],
        icon: "📋",
        category: "Queue Management"
      },
      {
        name: "router",
        title: "Router",
        description: "Get the Uniswap router address",
        inputs: [],
        icon: "🚀",
        category: "Basic Info"
      },
      {
        name: "sellPercent",
        title: "Sell Percent",
        description: "Get the sell percent",
        inputs: [],
        icon: "📉",
        category: "Trading",
        formatOutput: (value) => `${value}%`
      },
      {
        name: "totalNftsMinted",
        title: "Total NFTs Minted",
        description: "Get the total NFTs minted",
        inputs: [],
        icon: "🎨",
        category: "Stats"
      },
      {
        name: "usdtToken",
        title: "USDT Token",
        description: "Get the USDT token address",
        inputs: [],
        icon: "💰",
        category: "Basic Info"
      },
      {
        name: "checkQueue",
        title: "Check Queue",
        description: "Check if NFT is in queue",
        inputs: [
          { name: "tokenId", type: "uint256", placeholder: "Enter NFT token ID", required: true }
        ],
        icon: "🔍",
        category: "Queue Management",
        formatOutput: (value) => value ? 'Yes' : 'No'
      },
      {
        name: "getNftQueue",
        title: "Get NFT Queue",
        description: "Get the complete NFT queue as an array",
        inputs: [],
        icon: "📋",
        category: "Queue Management",
        formatOutput: (value) => value.join(', ') || 'Empty'
      },
      {
        name: "getNftQueue",
        title: "Get NFT Queue (Range)",
        description: "Get NFT queue items between startIndex and endIndex",
        inputs: [
          { name: "startIndex", type: "uint256", placeholder: "Enter start index", required: true },
          { name: "endIndex", type: "uint256", placeholder: "Enter end index", required: true }
        ],
        icon: "📋",
        category: "Queue Management",
        formatOutput: (value) => value.join(', ') || 'Empty'
      },
      {
        name: "nftQueueDeleted",
        title: "NFT Queue Deleted",
        description: "Check if NFT queue item at index is deleted",
        inputs: [
          { name: "arg0", type: "uint256", placeholder: "Enter index", required: true }
        ],
        icon: "🗑️",
        category: "Queue Management",
        formatOutput: (value) => value ? 'Yes' : 'No'
      },
      {
        name: "nftQueueTokenIndex",
        title: "NFT Queue Token Index",
        description: "Get queue position index for a token ID",
        inputs: [
          { name: "arg0", type: "uint256", placeholder: "Enter token ID", required: true }
        ],
        icon: "🔢",
        category: "Queue Management"
      },
      {
        name: "getNftSaleDetails",
        title: "Get NFT Sale Details",
        description: "Get detailed NFT sale information",
        inputs: [
          { name: "tokenId", type: "uint256", placeholder: "Enter NFT ID", required: true }
        ],
        icon: "📊",
        category: "Trading",
        formatOutput: (value) => {
          const info = value || {};
          return `Base Price: ${info.nftbasePrice ?? info[0] ?? '0'}\nCurrent Owner: ${info.nftCurrOwner ?? info[1] ?? '0x0'}\nIs Sale: ${info.isSale ?? info[2] ? 'Yes' : 'No'}\nBuy Timestamp: ${info.nftBuyTimeStamp ?? info[3] ?? '0'}\nSale Timestamp: ${info.nftSaleTimeStamp ?? info[4] ?? '0'}`;
        }
      },
      {
        name: "getTradexPrice",
        title: "Get Tradex Price",
        description: "Get Tradex price for USDT amount",
        inputs: [
          { name: "usdtAmount", type: "uint256", placeholder: "Enter USDT amount", required: true }
        ],
        icon: "💲",
        category: "Trading"
      },
      {
        name: "getEligibleTradeBots",
        title: "Eligible Trade Bots",
        description: "Get eligible trade bot list",
        inputs: [],
        icon: "🤖",
        category: "Trading",
        formatOutput: (value) => {
          const bots = Array.isArray(value) ? value : (Array.isArray(value?.[0]) ? value[0] : []);
          return bots.length ? bots.join(', ') : 'None';
        }
      },
      {
        name: "lastPurchaseAt",
        title: "Last Purchase At",
        description: "Get user's last purchase timestamp",
        inputs: [
          { name: "arg0", type: "address", placeholder: "Enter account address", required: true }
        ],
        icon: "🕒",
        category: "User Profile"
      },
      {
        name: "botInfo",
        title: "Bot Info",
        description: "Get bot info by account",
        inputs: [
          { name: "arg0", type: "address", placeholder: "Enter account address", required: true }
        ],
        icon: "🤖",
        category: "User Profile"
      },
      {
        name: "botById",
        title: "Bot By ID",
        description: "Get bot address by bot ID",
        inputs: [
          { name: "arg0", type: "uint256", placeholder: "Enter bot ID", required: true }
        ],
        icon: "🤖",
        category: "User Profile"
      },
      {
        name: "botCount",
        title: "Bot Count",
        description: "Get total number of registered bots",
        inputs: [],
        icon: "🤖",
        category: "Stats"
      },
      {
        name: "getBotAddress",
        title: "Get Bot Address",
        description: "Get bot address by bot ID",
        inputs: [
          { name: "botId", type: "uint256", placeholder: "Enter bot ID", required: true }
        ],
        icon: "🤖",
        category: "User Profile"
      },
      {
        name: "getBotId",
        title: "Get Bot ID",
        description: "Get bot ID by wallet address",
        inputs: [
          { name: "wallet", type: "address", placeholder: "Enter wallet address", required: true }
        ],
        icon: "🤖",
        category: "User Profile"
      },
      {
        name: "getUserAllNfts",
        title: "Get User All NFTs",
        description: "Get all NFTs owned by a user",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter user address", required: true }
        ],
        icon: "🎨",
        category: "User Profile",
        formatOutput: (value) => {
          const tokens = Array.isArray(value) ? value : (Array.isArray(value?.[0]) ? value[0] : []);
          return tokens.length ? tokens.join(', ') : 'None';
        }
      },
      {
        name: "getUserHoldLength",
        title: "User Hold Length",
        description: "Get number of NFTs currently held by user",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account address", required: true }
        ],
        icon: "📏",
        category: "User Profile"
      },
      {
        name: "getUserNextSale",
        title: "User Next Sale",
        description: "Get the next NFT scheduled for sale by user",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account address", required: true }
        ],
        icon: "⏭️",
        category: "User Profile"
      },
      {
        name: "getUserNftQueue",
        title: "Get User NFT Queue (Full)",
        description: "Get the full array of user's personal NFT queue",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account address", required: true }
        ],
        icon: "📋",
        category: "User Profile",
        formatOutput: (value) => {
          const tokens = Array.isArray(value) ? value : (Array.isArray(value?.[0]) ? value[0] : []);
          return tokens.length ? tokens.join(', ') : 'Empty';
        }
      },
      {
        name: "getUserOwnedNftAt",
        title: "Get User Owned NFT At",
        description: "Get user's owned NFT at index",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter user address", required: true },
          { name: "index", type: "uint256", placeholder: "Enter index", required: true }
        ],
        icon: "🔢",
        category: "User Profile"
      },
      {
        name: "getUserOwnedNftContains",
        title: "Get User Owned NFT Contains",
        description: "Check if user owns specific NFT",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter user address", required: true },
          { name: "tokenId", type: "uint256", placeholder: "Enter token ID", required: true }
        ],
        icon: "🔍",
        category: "User Profile",
        formatOutput: (value) => value ? 'Yes' : 'No'
      },
      {
        name: "getUserOwnedNftLength",
        title: "Get User Owned NFT Length",
        description: "Get number of NFTs owned by user",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter user address", required: true }
        ],
        icon: "📏",
        category: "User Profile"
      },
      {
        name: "lpReward",
        title: "LP Reward",
        description: "Get the LP reward contract address",
        inputs: [],
        icon: "🏆",
        category: "Basic Info"
      },
      {
        name: "nftQueue",
        title: "NFT Queue (Specific)",
        description: "Get NFT queue value at index",
        inputs: [
          { name: "index", type: "uint256", placeholder: "Enter index", required: true }
        ],
        icon: "📋",
        category: "Queue Management"
      },
      {
        name: "nftSale",
        title: "NFT Sale",
        description: "Get NFT sale information",
        inputs: [
          { name: "nftId", type: "uint256", placeholder: "Enter NFT ID", required: true }
        ],
        icon: "🏪",
        category: "Trading",
        formatOutput: (value) => {
          const info = value || {};
          return `NFT Base Price: ${info.nftBasePrice ?? info[0] ?? '0'}\nLast Sale Timestamp: ${info.lastSaleTimeStamp ?? info[1] ?? '0'}\nNFT Owner: ${info.nftOwner ?? info[2] ?? '0x0'}\nIs Sale: ${info.isSale ?? info[3] ? 'Yes' : 'No'}`;
        }
      },
      {
        name: "nftSalePrice",
        title: "NFT Sale Price",
        description: "Get sale price of NFT",
        inputs: [
          { name: "tokenId", type: "uint256", placeholder: "Enter token ID", required: true }
        ],
        icon: "💲",
        category: "Trading"
      },
      {
        name: "nftTimeInfo",
        title: "NFT Time Info",
        description: "Get buy and sale timestamps for NFT",
        inputs: [
          { name: "nftId", type: "uint256", placeholder: "Enter NFT ID", required: true }
        ],
        icon: "⏰",
        category: "Trading",
        formatOutput: (value) => {
          const info = value || {};
          return `Buy Timestamp: ${info.nftBuyTimeStamp ?? info[0] ?? '0'}\nSale Timestamp: ${info.nftSaleTimeStamp ?? info[1] ?? '0'}`;
        }
      },
      {
        name: "userDayPurchase",
        title: "User Day Purchase",
        description: "Get user purchase amount for a specific day",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account address", required: true },
          { name: "day", type: "uint256", placeholder: "Enter day", required: true }
        ],
        icon: "📅",
        category: "User Profile"
      },
      {
        name: "userNftQueue",
        title: "User NFT Queue (Specific)",
        description: "Get user NFT queue value at index",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account address", required: true },
          { name: "index", type: "uint256", placeholder: "Enter index", required: true }
        ],
        icon: "📋",
        category: "User Profile"
      },
      {
        name: "userPaymentMode",
        title: "User Payment Mode",
        description: "Get user payment mode for NFT",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account address", required: true },
          { name: "index", type: "uint256", placeholder: "Enter index", required: true }
        ],
        icon: "💳",
        category: "User Profile",
        formatOutput: (value) => value ? 'Coin' : 'Other'
      },
      {
        name: "userProfiles",
        title: "User Profiles",
        description: "Get user profile information",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account address", required: true }
        ],
        icon: "👥",
        category: "User Profile",
        formatOutput: (value) => {
          const info = value || {};
          return `User Hold Start: ${info.userHoldStart ?? info[0] ?? '0'}\nUser Hold End: ${info.userHoldEnd ?? info[1] ?? '0'}\nLast Purchase Timestamp: ${info.lastPurchaseTimestamp ?? info[2] ?? '0'}\nUser Last Cycle: ${info.userLastCycle ?? info[3] ?? '0'}\nUser Last Draw: ${info.userLastDraw ?? info[4] ?? '0'}\nIs Exclude Condition: ${info.isExcludeCondition ?? info[5] ? 'Yes' : 'No'}\nIs Blocked: ${info.isBlocked ?? info[6] ? 'Yes' : 'No'}`;
        }
      }
    ],
    
    // Write Functions (Transaction Functions)
    writeFunctions: [
      {
        name: "buyNft",
        title: "Buy NFT",
        description: "Buy an NFT",
        inputs: [],
        requiresPayment: false,
        requiresApproval: true,
        icon: "🛒",
        category: "User Actions"
      },
      {
        name: "enableAutoTrade",
        title: "Enable Auto Trade",
        description: "Enable or disable auto trading",
        inputs: [
          { name: "status", type: "bool", placeholder: "true/false", required: true },
          { name: "level", type: "uint8", placeholder: "Enter level (uint8)", required: true }
        ],
        requiresPayment: false,
        icon: "🤖",
        category: "User Actions"
      },
      {
        name: "checkBotStatusExternal",
        title: "Check Bot Status External",
        description: "Update external bot status for an account",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account address", required: true }
        ],
        requiresPayment: false,
        icon: "🧪",
        category: "User Actions"
      },
      {
        name: "autoTradeBatch",
        title: "Auto Trade Batch",
        description: "Run auto-trade for token batch",
        inputs: [
          { name: "tokenIds", type: "uint256[]", placeholder: "Enter token IDs (comma separated)", required: true },
          { name: "bots", type: "address[]", placeholder: "Enter bot addresses (comma separated)", required: true }
        ],
        requiresPayment: false,
        icon: "⚡",
        category: "User Actions"
      },
      {
        name: "burnNftReward",
        title: "Burn NFT Reward",
        description: "Burn NFT and process reward",
        inputs: [
          { name: "nftId", type: "uint256", placeholder: "Enter NFT ID", required: true }
        ],
        requiresPayment: false,
        icon: "🏆",
        category: "User Actions"
      },
      {
        name: "claimNft",
        title: "Claim NFT",
        description: "Claim NFT",
        inputs: [],
        requiresPayment: false,
        icon: "🎁",
        category: "User Actions"
      },
      {
        name: "placeOrder",
        title: "Place Order",
        description: "Place order for NFT",
        inputs: [
          { name: "nftId", type: "uint256", placeholder: "Enter NFT ID", required: true }
        ],
        requiresPayment: false,
        icon: "📝",
        category: "User Actions"
      },
      {
        name: "burnNftId",
        title: "Burn NFT ID",
        description: "Burn NFT by ID",
        inputs: [
          { name: "nftTokenId", type: "uint256", placeholder: "Enter NFT token ID", required: true }
        ],
        requiresPayment: false,
        icon: "🔥",
        category: "User Actions"
      },
      {
        name: "bulkNurnNftId",
        title: "Bulk Burn NFT ID",
        description: "Bulk burn NFT IDs",
        inputs: [
          { name: "tokenIds", type: "uint256[]", placeholder: "Enter token IDs (comma separated)", required: true }
        ],
        requiresPayment: false,
        icon: "🔥",
        category: "User Actions"
      },
      {
        name: "burnExpiredNft",
        title: "Burn Expired NFT",
        description: "Burn an expired NFT",
        inputs: [
          { name: "nftId", type: "uint256", placeholder: "Enter NFT ID", required: true }
        ],
        requiresPayment: false,
        icon: "🔥",
        category: "User Actions"
      }
    ],

    // Admin Functions
    adminFunctions: [
      {
        name: "initialize",
        title: "Initialize",
        description: "Initialize contract",
        inputs: [
          { name: "initialOwner", type: "address", placeholder: "Enter initial owner", required: true },
          { name: "tradexTokenAddress", type: "address", placeholder: "Enter Tradex token address", required: true }
        ],
        requiresPayment: false,
        icon: "⚙️",
        category: "Admin Control"
      },
      // {
      //   name: "initialize",
      //   title: "Initialize",
      //   description: "Initialize contract",
      //   inputs: [
      //     { name: "initialOwner", type: "address", placeholder: "Enter initial owner", required: true },
      //     { name: "usdtTokenAddress", type: "address", placeholder: "Enter USDT token address", required: true },
      //     { name: "FoxTokenAddress", type: "address", placeholder: "Enter Fox token address", required: true }
      //   ],
      //   requiresPayment: false,
      //   icon: "⚙️",
      //   category: "Admin Control"
      // },
      {
        name: "onERC721Received",
        title: "On ERC721 Received",
        description: "ERC721 receiver handler",
        inputs: [
          { name: "operator", type: "address", placeholder: "Enter operator address", required: true },
          { name: "from", type: "address", placeholder: "Enter from address", required: true },
          { name: "tokenId", type: "uint256", placeholder: "Enter token ID", required: true },
          { name: "data", type: "bytes", placeholder: "Enter bytes data (0x...)", required: true }
        ],
        requiresPayment: false,
        icon: "🎨",
        category: "User Actions"
      },
      {
        name: "pause",
        title: "Pause",
        description: "Pause contract",
        inputs: [],
        requiresPayment: false,
        icon: "⏸️",
        category: "Admin Control"
      },
      {
        name: "unpause",
        title: "Unpause",
        description: "Unpause contract",
        inputs: [],
        requiresPayment: false,
        icon: "▶️",
        category: "Admin Control"
      },
      {
        name: "renounceOwnership",
        title: "Renounce Ownership",
        description: "Renounce ownership",
        inputs: [],
        requiresPayment: false,
        icon: "👑",
        category: "Admin Control"
      },
      {
        name: "transferOwnership",
        title: "Transfer Ownership",
        description: "Transfer ownership",
        inputs: [
          { name: "newOwner", type: "address", placeholder: "Enter new owner", required: true }
        ],
        requiresPayment: false,
        icon: "🔄",
        category: "Admin Control"
      },
      {
        name: "setClaimStatus",
        title: "Set Claim Status",
        description: "Set claim status",
        inputs: [
          { name: "status", type: "bool", placeholder: "true/false", required: true }
        ],
        requiresPayment: false,
        icon: "✅",
        category: "Admin Settings"
      },
      {
        name: "setGlobalPeriod",
        title: "Set Global Period",
        description: "Set global period",
        inputs: [
          { name: "newGlobalPeriod", type: "uint256", placeholder: "Enter new global period", required: true }
        ],
        requiresPayment: false,
        icon: "⏱️",
        category: "Admin Settings"
      },
      {
        name: "setMaxNftSupply",
        title: "Set Max NFT Supply",
        description: "Set max NFT supply",
        inputs: [
          { name: "newSupply", type: "uint256", placeholder: "Enter new supply", required: true }
        ],
        requiresPayment: false,
        icon: "📈",
        category: "Admin Settings"
      },
      {
        name: "setMaxSalePrice",
        title: "Set Max Sale Price",
        description: "Set max sale price",
        inputs: [
          { name: "newMaxSalePrice", type: "uint256", placeholder: "Enter new max sale price", required: true }
        ],
        requiresPayment: false,
        icon: "💲",
        category: "Admin Settings"
      },
      {
        name: "setNftBurnBasePrice",
        title: "Set NFT Burn Base Price",
        description: "Set NFT burn base price",
        inputs: [
          { name: "newNftBurnBasePrice", type: "uint256", placeholder: "Enter new NFT burn base price", required: true }
        ],
        requiresPayment: false,
        icon: "🔥",
        category: "Admin Settings"
      },
      {
        name: "setNftContract",
        title: "Set NFT Contract",
        description: "Set NFT contract",
        inputs: [
          { name: "newNftAddress", type: "address", placeholder: "Enter new NFT address", required: true }
        ],
        requiresPayment: false,
        icon: "🎨",
        category: "Admin Settings"
      },
      {
        name: "setNftMatrixContract",
        title: "Set NFT Matrix Contract",
        description: "Set NFT matrix contract",
        inputs: [
          { name: "newNftMatrix", type: "address", placeholder: "Enter new NFT matrix", required: true }
        ],
        requiresPayment: false,
        icon: "🤖",
        category: "Admin Settings"
      },
      {
        name: "setNftPrice",
        title: "Set NFT Price",
        description: "Set NFT price",
        inputs: [
          { name: "newPrice", type: "uint256", placeholder: "Enter new price", required: true }
        ],
        requiresPayment: false,
        icon: "💵",
        category: "Admin Settings"
      },
      {
        name: "setNftRewardContract",
        title: "Set NFT Reward Contract",
        description: "Set NFT reward contract",
        inputs: [
          { name: "newNftReward", type: "address", placeholder: "Enter new NFT reward address", required: true }
        ],
        requiresPayment: false,
        icon: "🏆",
        category: "Admin Settings"
      },
      {
        name: "setPlatformRewardReceiver",
        title: "Set Platform Reward Receiver",
        description: "Set platform reward receiver",
        inputs: [
          { name: "newPlatformRewardReceiver", type: "address", placeholder: "Enter new platform reward receiver", required: true }
        ],
        requiresPayment: false,
        icon: "📤",
        category: "Admin Settings"
      },
      {
        name: "setQueueRequire",
        title: "Set Queue Require",
        description: "Set queue require",
        inputs: [
          { name: "newQueueRequire", type: "uint256", placeholder: "Enter new queue require", required: true }
        ],
        requiresPayment: false,
        icon: "📋",
        category: "Admin Settings"
      },
      {
        name: "setRouterAndPair",
        title: "Set Router And Pair",
        description: "Set Uniswap router and pair",
        inputs: [
          { name: "newRouter", type: "address", placeholder: "Enter new router", required: true },
          { name: "newPair", type: "address", placeholder: "Enter new pair", required: true }
        ],
        requiresPayment: false,
        icon: "🔗",
        category: "Admin Settings"
      },
      {
        name: "setSaleIncomePercent",
        title: "Set Sale Income Percent",
        description: "Set sale income percents",
        inputs: [
          { name: "newNftOwnerIncomePercent", type: "uint256", placeholder: "Enter new NFT owner income percent", required: true },
          { name: "newLevelIncomePercent", type: "uint256", placeholder: "Enter new level income percent", required: true },
          { name: "newBuringIncomePercent", type: "uint256", placeholder: "Enter new burning income percent", required: true },
          { name: "newLpIncomePercent", type: "uint256", placeholder: "Enter new LP income percent", required: true },
          { name: "newPlatformIncomePercent", type: "uint256", placeholder: "Enter new platform income percent", required: true }
        ],
        requiresPayment: false,
        icon: "📊",
        category: "Admin Settings"
      },
      {
        name: "setSellPercent",
        title: "Set Sell Percent",
        description: "Set sell percent",
        inputs: [
          { name: "newSellPercent", type: "uint256", placeholder: "Enter new sell percent", required: true }
        ],
        requiresPayment: false,
        icon: "📉",
        category: "Admin Settings"
      },
      {
        name: "setTradexToken",
        title: "Set Tradex Token",
        description: "Set Tradex token",
        inputs: [
          { name: "newTradexToken", type: "address", placeholder: "Enter new Tradex token", required: true }
        ],
        requiresPayment: false,
        icon: "🪐",
        category: "Admin Settings"
      },
      {
        name: "setAutoTradeFee",
        title: "Set Auto Trade Fee",
        description: "Set auto trade fee",
        inputs: [
          { name: "newFee", type: "uint256", placeholder: "Enter new fee", required: true }
        ],
        requiresPayment: false,
        icon: "🤖",
        category: "Admin Settings"
      },
      {
        name: "setAutoTradeFeeEnable",
        title: "Set Auto Trade Fee Enable",
        description: "Enable/disable auto trade fee",
        inputs: [
          { name: "status", type: "bool", placeholder: "true/false", required: true }
        ],
        requiresPayment: false,
        icon: "✅",
        category: "Admin Settings"
      },
      {
        name: "setAutoTradeFeeReceiver",
        title: "Set Auto Trade Fee Receiver",
        description: "Set auto trade fee receiver",
        inputs: [
          { name: "newWallet", type: "address", placeholder: "Enter new wallet", required: true }
        ],
        requiresPayment: false,
        icon: "📥",
        category: "Admin Settings"
      },
      {
        name: "setBotTimeDuration",
        title: "Set Bot Time Duration",
        description: "Set bot time duration",
        inputs: [
          { name: "newBotTimeDuration", type: "uint256", placeholder: "Enter new duration", required: true }
        ],
        requiresPayment: false,
        icon: "⏱️",
        category: "Admin Settings"
      },
      {
        name: "setLastPurchaseAt",
        title: "Set Last Purchase At",
        description: "Set user's last purchase period",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account", required: true },
          { name: "newPeriod", type: "uint256", placeholder: "Enter new period", required: true }
        ],
        requiresPayment: false,
        icon: "🕒",
        category: "Admin Settings"
      },
      {
        name: "setUsdtToken",
        title: "Set USDT Token",
        description: "Set USDT token",
        inputs: [
          { name: "newUsdtToken", type: "address", placeholder: "Enter new USDT token", required: true }
        ],
        requiresPayment: false,
        icon: "💰",
        category: "Admin Settings"
      },
      {
        name: "setBurnAndMint",
        title: "Set Burn And Mint",
        description: "Set burn and mint",
        inputs: [
          { name: "newMintNftCount", type: "uint256", placeholder: "Enter new mint NFT count", required: true },
          { name: "newMaxSalePrice", type: "uint256", placeholder: "Enter new max sale price", required: true }
        ],
        requiresPayment: false,
        icon: "🔥",
        category: "Admin Settings"
      },
      {
        name: "setLpRewardContract",
        title: "Set LP Reward Contract",
        description: "Set LP reward contract",
        inputs: [
          { name: "newLpReward", type: "address", placeholder: "Enter new LP reward address", required: true }
        ],
        requiresPayment: false,
        icon: "🏆",
        category: "Admin Settings"
      },
      {
        name: "setUserBlockRewardCondition",
        title: "Set User Block Reward Condition",
        description: "Set user block reward condition",
        inputs: [
          { name: "userAccount", type: "address", placeholder: "Enter user account", required: true },
          { name: "status", type: "bool", placeholder: "true/false", required: true }
        ],
        requiresPayment: false,
        icon: "🚫",
        category: "Admin Settings"
      },
      {
        name: "setUserExcludeRewardCondition",
        title: "Set User Exclude Reward Condition",
        description: "Set user exclude reward condition",
        inputs: [
          { name: "userAccount", type: "address", placeholder: "Enter user account", required: true },
          { name: "status", type: "bool", placeholder: "true/false", required: true }
        ],
        requiresPayment: false,
        icon: "❌",
        category: "Admin Settings"
      },
      {
        name: "setUserPayment",
        title: "Set User Payment",
        description: "Set user payment modes for NFTs",
        inputs: [
          { name: "nftTokenIds", type: "uint256[]", placeholder: "Enter NFT token IDs (comma separated)", required: true },
          { name: "isCoin", type: "bool[]", placeholder: "Enter booleans (comma separated true/false)", required: true }
        ],
        requiresPayment: false,
        icon: "💳",
        category: "Admin Settings"
      },
      {
        name: "setnftDeadDuration",
        title: "Set NFT Dead Duration",
        description: "Set NFT dead duration",
        inputs: [
          { name: "newNftDeadDuration", type: "uint256", placeholder: "Enter new NFT dead duration", required: true }
        ],
        requiresPayment: false,
        icon: "⏳",
        category: "Admin Settings"
      },
      {
        name: "burnNftFromOwner",
        title: "Burn NFT From Owner",
        description: "Burn NFT from owner",
        inputs: [
          { name: "nftId", type: "uint256", placeholder: "Enter NFT ID", required: true }
        ],
        requiresPayment: false,
        icon: "🔥",
        category: "Admin Control"
      },
      {
        name: "recoverErc20",
        title: "Recover ERC20",
        description: "Recover ERC20",
        inputs: [
          { name: "erc20Token", type: "address", placeholder: "Enter ERC20 token", required: true },
          { name: "amount", type: "uint256", placeholder: "Enter amount", required: true }
        ],
        requiresPayment: false,
        icon: "🔄",
        category: "Admin Recovery"
      },
      {
        name: "recoverEth",
        title: "Recover Eth",
        description: "Recover Eth",
        inputs: [
          { name: "amount", type: "uint256", placeholder: "Enter amount", required: true }
        ],
        requiresPayment: false,
        icon: "💎",
        category: "Admin Recovery"
      },
      {
        name: "recoverNft",
        title: "Recover NFT",
        description: "Recover NFT",
        inputs: [
          { name: "receiver", type: "address", placeholder: "Enter receiver", required: true },
          { name: "nftId", type: "uint256", placeholder: "Enter NFT ID", required: true }
        ],
        requiresPayment: false,
        icon: "🎨",
        category: "Admin Recovery"
      },
      {
        name: "transferNftToOwner",
        title: "Transfer NFT To Owner",
        description: "Transfer NFT to owner",
        inputs: [
          { name: "start", type: "uint256", placeholder: "Enter start", required: true },
          { name: "end", type: "uint256", placeholder: "Enter end", required: true }
        ],
        requiresPayment: false,
        icon: "📤",
        category: "Admin Transfer"
      },
      {
        name: "transferNftToUser",
        title: "Transfer NFT To User",
        description: "Transfer NFT to user",
        inputs: [
          { name: "accounts", type: "address[]", placeholder: "Enter accounts (comma separated)", required: true },
          { name: "tokenIds", type: "uint256[]", placeholder: "Enter token IDs (comma separated)", required: true }
        ],
        requiresPayment: false,
        icon: "📤",
        category: "Admin Transfer"
      },
      {
        name: "bulkImportBots",
        title: "Bulk Import Bots",
        description: "Bulk import bot accounts",
        inputs: [
          { name: "accounts", type: "address[]", placeholder: "Enter bot addresses (comma separated)", required: true }
        ],
        requiresPayment: false,
        icon: "🤖",
        category: "Admin Control"
      },
      {
        name: "compactQueue",
        title: "Compact Queue",
        description: "Compact the NFT queue by removing deleted entries",
        inputs: [
          { name: "maxSteps", type: "uint256", placeholder: "Enter max steps", required: true }
        ],
        requiresPayment: false,
        icon: "🗜️",
        category: "Admin Control"
      },
      {
        name: "newSetUp",
        title: "New Setup",
        description: "Run new setup configuration on contract",
        inputs: [],
        requiresPayment: false,
        icon: "⚙️",
        category: "Admin Control"
      }
    ]
  };

  const connectWallet = async () => {
    setLoadingStates(prev => ({ ...prev, connect: true }));
    
    try {
      const acc = await loadWeb3();
      if (acc === "No Wallet") {
        toast.error("No Wallet Detected");
        setBtnTxt("Connect Wallet");
      } else if (acc === "Wrong Network") {
        toast.error("Please switch to opBNB Mainnet");
        setBtnTxt("Wrong Network");
      } else {
        setAccount(acc);
        setBtnTxt(acc.substring(0, 6) + '...' + acc.substring(acc.length - 4));
        toast.success("Connected to opBNB Mainnet");
        
        loadMarketplaceData();
      }
    } catch (error) {
      console.error("Connection error:", error);
      toast.error("Connection failed");
      setBtnTxt("Connect Wallet");
    } finally {
      setLoadingStates(prev => ({ ...prev, connect: false }));
    }
  };

  const getContract = () => {
    const web3 = getWeb3Instance();
    return new web3.eth.Contract(tradeXNftMarketplaceAbi, tradeXNftMarketplace);
  };

  const getUsdtContract = () => {
    const web3 = getWeb3Instance();
    return new web3.eth.Contract(USDTAbi, USDT);
  };

  // Load marketplace overview data
  const loadMarketplaceData = useCallback(async () => {
    if (!account) return;
    
    try {
      const web3 = getWeb3Instance();
      const contract = getContract();
      const usdtContract = getUsdtContract();
      
      const [totalNftsMinted, maxNftSupply, nftBasePrice, nftQueueLength, isClaimEnable, contractUsdtBalance, userUsdtBalance] = await Promise.all([
        contract.methods.totalNftsMinted().call().catch(() => "0"),
        contract.methods.maxNftSupply().call().catch(() => "0"),
        contract.methods.nftBasePrice().call().catch(() => "0"),
        contract.methods.nftQueueLength().call().catch(() => "0"),
        contract.methods.isClaimEnable().call().catch(() => false),
        usdtContract.methods.balanceOf(tradeXNftMarketplace).call().catch(() => "0"),
        usdtContract.methods.balanceOf(account).call().catch(() => "0")
      ]);

      setMarketplaceData({
        totalNftsMinted: totalNftsMinted.toString(),
        maxNftSupply: maxNftSupply.toString(),
        nftBasePrice: web3.utils.fromWei(nftBasePrice.toString(), 'ether'),
        queueLength: nftQueueLength.toString(),
        isClaimEnabled: isClaimEnable,
        usdtBalance: parseFloat(web3.utils.fromWei(contractUsdtBalance.toString(), 'ether')).toFixed(2),
        userUsdtBalance: parseFloat(web3.utils.fromWei(userUsdtBalance.toString(), 'ether')).toFixed(2)
      });
      
    } catch (error) {
      console.error("Error loading marketplace data:", error);
      toast.error("Failed to load marketplace data");
    }
  }, [account]);

  useEffect(() => {
    if (account) {
      loadMarketplaceData();
    }
  }, [account, loadMarketplaceData]);

  // Handle input changes for any function
  const handleInputChange = (functionName, inputName, value) => {
    setFunctionInputs(prev => ({
      ...prev,
      [functionName]: {
        ...prev[functionName],
        [inputName]: value
      }
    }));
  };

  // Execute read functions
  const executeReadFunction = async (functionData) => {
    if (!account) {
      toast.error("Please connect your wallet first");
      return;
    }

    const funcKey = functionData.name;
    setLoadingStates(prev => ({ ...prev, [funcKey]: true }));
    try {
      const contract = getContract();
      const inputs = functionInputs[funcKey] || {};
      
      const args = functionData.inputs.map(input => {
        const value = inputs[input.name];
        if (input.required && (!value || value.trim() === '')) {
          throw new Error(`${input.name} is required`);
        }
        if (input.type.includes('[]') && typeof value === 'string') {
          return value.split(',').map(v => v.trim());
        }
        return value || '';
      });

      let result;
      try {
        result = await contract.methods[functionData.name](...args).call();
      } catch (decodeErr) {
        if (decodeErr.message && (decodeErr.message.includes('boolean') || decodeErr.message.includes('Parameter decoding'))) {
          // Fallback: raw eth_call + lenient bool decoding (web3 v4 strict bool issue)
          const web3 = getWeb3Instance();
          const callData = contract.methods[functionData.name](...args).encodeABI();
          const rawHex = await web3.eth.call({ to: tradeXNftMarketplace, data: callData });
          const abiFn = tradeXNftMarketplaceAbi.find(f => f.name === functionData.name && f.type === 'function');
          if (abiFn && abiFn.outputs && abiFn.outputs.length > 0) {
            const lenientTypes = abiFn.outputs.map(o => ({ ...o, type: o.type === 'bool' ? 'uint8' : o.type }));
            const decoded = web3.eth.abi.decodeParameters(lenientTypes, rawHex);
            const namedResult = {};
            abiFn.outputs.forEach((output, i) => {
              const key = output.name || i.toString();
              const val = decoded[i];
              if (output.type === 'bool') {
                namedResult[key] = Number(val) === 1 ? true : false;
              } else {
                namedResult[key] = typeof val === 'bigint' ? val.toString() : val;
              }
            });
            result = namedResult;
          } else {
            throw decodeErr;
          }
        } else {
          throw decodeErr;
        }
      }

      const formattedResult = functionData.formatOutput
        ? functionData.formatOutput(result)
        : JSON.stringify(result, (key, value) => typeof value === 'bigint' ? value.toString() : value, 2);

      setFunctionOutputs(prev => ({
        ...prev,
        [funcKey]: formattedResult
      }));

      toast.success("Read operation successful!");
    } catch (error) {
      console.error("Read error:", error);
      toast.error(error.message || "Read operation failed");
    } finally {
      setLoadingStates(prev => ({ ...prev, [funcKey]: false }));
    }
  };

  // Execute write functions
  const executeWriteFunction = async (functionData) => {
    if (!account) {
      toast.error("Please connect your wallet first");
      return;
    }

    const funcKey = functionData.name;
    setLoadingStates(prev => ({ ...prev, [funcKey]: true }));
    try {
      const web3 = getWeb3Instance();
      const contract = getContract();
      const usdtContract = getUsdtContract();
      const inputs = functionInputs[funcKey] || {};
      
      const args = functionData.inputs.map(input => {
        const value = inputs[input.name];
        if (input.required && (!value || value.trim() === '')) {
          throw new Error(`${input.name} is required`);
        }
        if (input.type.includes('[]') && typeof value === 'string') {
          return value.split(',').map(v => v.trim());
        }
        if (input.type === 'bool') {
          return typeof value === 'boolean' ? value : String(value).toLowerCase() === 'true';
        }
        if (input.type === 'bool[]' && typeof value === 'string') {
          return value.split(',').map(v => v.trim().toLowerCase() === 'true');
        }
        return value || '';
      });

      if (functionData.requiresApproval) {
        const usdtAmount = await contract.methods.nftBasePrice().call(); // Assuming buyNft costs nftBasePrice
        const allowance = await usdtContract.methods.allowance(account, tradeXNftMarketplace).call();
        if (BigInt(allowance) < BigInt(usdtAmount)) {
          toast.info("Approving USDT...");
          await usdtContract.methods.approve(tradeXNftMarketplace, usdtAmount).send({
            from: account
          });
          toast.success("USDT Approved!");
        }
      }

      const tx = await contract.methods[functionData.name](...args).send({
        from: account
      });

      toast.success(`Transaction successful! Hash: ${tx.transactionHash}`);
      loadMarketplaceData();
    } catch (error) {
      console.error("Write error:", error);
      toast.error(error.message || "Write operation failed");
    } finally {
      setLoadingStates(prev => ({ ...prev, [funcKey]: false }));
    }
  };

  // Group functions by category
  const groupFunctionsByCategory = (functions) => {
    return functions.reduce((acc, func) => {
      const cat = func.category || 'Other';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(func);
      return acc;
    }, {});
  };

  // Render function card
  const renderFunctionCard = (func, isWrite = false, isAdmin = false) => {
    const funcKey = func.name;
    const inputs = functionInputs[funcKey] || {};
    const output = functionOutputs[funcKey];
    const isLoading = loadingStates[funcKey] || false;

    return (
      <div key={funcKey} className="col-lg-4 col-md-6 mb-4">
        <div className="card h-100 shadow-lg border-0" style={{
          borderRadius: '20px',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))',
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <div className="card-body p-4 d-flex flex-column">
            <div className="d-flex align-items-center mb-3">
              <span className="fs-3 me-3">{func.icon}</span>
              <div>
                <h5 className="mb-1 text-white">{func.title}</h5>
                <small className="text-white-50">{func.description}</small>
              </div>
            </div>

            {func.inputs.map(input => (
              <div key={input.name} className="mb-3">
                <input
                  type="text"
                  className="form-control bg-dark border-0 text-white placeholder-white"
                  placeholder={input.placeholder}
                  value={inputs[input.name] || ''}
                  onChange={(e) => handleInputChange(funcKey, input.name, e.target.value)}
                  style={{
                    borderRadius: '12px',
                    padding: '12px'
                  }}
                />
              </div>
            ))}

            <button 
              onClick={() => isWrite || isAdmin 
                ? executeWriteFunction(func)
                : executeReadFunction(func)}
              disabled={isLoading}
              className="btn w-100 mt-auto"
              style={{
                background: isWrite || isAdmin 
                  ? 'linear-gradient(135deg, #ff6b6b, #4ecdc4)' 
                  : 'linear-gradient(135deg, #00ff88, #00ccff)',
                border: 'none',
                color: '#fff',
                borderRadius: '12px',
                padding: '12px',
                fontWeight: '600'
              }}
            >
              {isLoading ? 'Processing...' : (isWrite || isAdmin ? 'Execute Write' : 'Read')}
            </button>

            {output && (
              <div className="mt-3 p-3 rounded-3" style={{
                background: 'rgba(0, 255, 136, 0.1)',
                border: '1px solid rgba(0, 255, 136, 0.3)',
                whiteSpace: 'pre-wrap',
                overflow: 'auto',
                maxHeight: '200px'
              }}>
                <small className="text-white-50 d-block mb-2">Output:</small>
                <code className="text-white">{output}</code>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center p-4" style={{
      background: 'linear-gradient(135deg, #000428, #004e92)'
    }}>
      <div className="container text-center">
        <h1 className="mb-5 display-4 fw-bold" style={{
          background: 'linear-gradient(135deg, #00ff88, #00ccff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          TradeXNFT Marketplace Dashboard
        </h1>

        <div className="mb-5">
          <button 
            onClick={connectWallet}
            disabled={loadingStates.connect}
            className="btn px-4 px-md-5 py-3 rounded-pill shadow-lg position-relative overflow-hidden"
            style={{ 
              background: account ? 
                'linear-gradient(135deg, #00ff88, #00ccff)' : 
                'linear-gradient(135deg, #ff6b6b, #4ecdc4)',
              border: 'none',
              color: account ? '#000' : '#fff',
              fontSize: 'clamp(0.9rem, 3vw, 1.1rem)', 
              fontWeight: '700',
              transition: 'all 0.3s ease',
              minWidth: '160px',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
              opacity: loadingStates.connect ? 0.7 : 1
            }}
          >
            {loadingStates.connect ? (
              <div className="d-flex align-items-center">
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                Connecting...
              </div>
            ) : (
              <span className="position-relative">{btnTxt}</span>
            )}
          </button>
        </div>
        
        {account && (
          <div className="mt-4 p-3 rounded-4" style={{
            background: 'rgba(0, 255, 136, 0.1)',
            border: '1px solid rgba(0, 255, 136, 0.3)'
          }}>
            <div className="d-flex justify-content-center align-items-center gap-2">
              <span className="text-white-50 small">
                🔗 {account.substring(0, 6)}...{account.substring(account.length - 4)}
              </span>
              <span className="badge bg-success rounded-pill">Connected</span>
            </div>
          </div>
        )}

        {account && (
          <div className="row">
            <div className="col-12">
              <div className="card shadow-lg border-0 mb-4" style={{ 
                borderRadius: '24px',
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div className="card-header border-0 p-4" style={{ 
                  borderRadius: '24px 24px 0 0',
                  background: 'transparent'
                }}>
                  <ul className="nav nav-pills justify-content-center flex-wrap gap-2" role="tablist">
                    {[
                      { key: 'overview', label: 'Overview', icon: '🏪', color: '#00ff88' },
                      { key: 'read', label: 'Read Functions', icon: '👁️', color: '#45b7d1' },
                      { key: 'write', label: 'Write Functions', icon: '📝', color: '#4ecdc4' },
                      { key: 'admin', label: 'Admin Functions', icon: '⚙️', color: '#ff6b6b' }
                    ].map(tab => (
                      <li key={tab.key} className="nav-item">
                        <button
                          className={`nav-link border-0 px-4 py-3 fw-bold position-relative overflow-hidden ${
                            activeTab === tab.key ? 'text-dark' : 'text-white-50'
                          }`}
                          onClick={() => setActiveTab(tab.key)}
                          style={{
                            borderRadius: '16px',
                            background: activeTab === tab.key ? 
                              `linear-gradient(135deg, ${tab.color}, ${tab.color}80)` : 
                              'rgba(255, 255, 255, 0.05)',
                            border: activeTab === tab.key ? 
                              `1px solid ${tab.color}` : 
                              '1px solid rgba(255, 255, 255, 0.1)',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            transform: activeTab === tab.key ? 'translateY(-2px)' : 'translateY(0)',
                            boxShadow: activeTab === tab.key ? 
                              `0 8px 25px ${tab.color}40` : 'none'
                          }}
                        >
                          <span className="me-2 fs-5">{tab.icon}</span>
                          <span className="d-none d-md-inline">{tab.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="card-body p-4" style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '0 0 24px 24px'
                }}>
                  {/* Overview Tab */}
                  {activeTab === 'overview' && (
                    <div className="row g-4">
                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, rgba(0, 255, 136, 0.15), rgba(69, 183, 209, 0.1))',
                          border: '1px solid rgba(0, 255, 136, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">🎨</div>
                            <h4 className="text-white mb-2">{marketplaceData.totalNftsMinted || '0'}</h4>
                            <p className="text-white-50 mb-0">Total Minted</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={() => executeReadFunction({name: 'totalNftsMinted', title: 'Total NFTs Minted', inputs: []})}
                              style={{
                                background: 'rgba(0, 255, 136, 0.2)',
                                border: '1px solid #00ff88',
                                color: '#00ff88'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, rgba(69, 183, 209, 0.15), rgba(107, 224, 215, 0.1))',
                          border: '1px solid rgba(69, 183, 209, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">📈</div>
                            <h4 className="text-white mb-2">{marketplaceData.maxNftSupply || '0'}</h4>
                            <p className="text-white-50 mb-0">Max Supply</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={() => executeReadFunction({name: 'maxNftSupply', title: 'Max NFT Supply', inputs: []})}
                              style={{
                                background: 'rgba(69, 183, 209, 0.2)',
                                border: '1px solid #45b7d1',
                                color: '#45b7d1'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, rgba(107, 224, 215, 0.15), rgba(255, 170, 0, 0.1))',
                          border: '1px solid rgba(107, 224, 215, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">💵</div>
                            <h4 className="text-white mb-2">{marketplaceData.nftBasePrice || '0'}</h4>
                            <p className="text-white-50 mb-0">Base Price (USDT)</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={() => executeReadFunction({name: 'nftBasePrice', title: 'NFT Base Price', inputs: []})}
                              style={{
                                background: 'rgba(107, 224, 215, 0.2)',
                                border: '1px solid #6be0d7',
                                color: '#6be0d7'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: `linear-gradient(135deg, ${marketplaceData.isClaimEnabled ? 'rgba(0, 255, 136, 0.15)' : 'rgba(255, 107, 107, 0.15)'}, rgba(255, 170, 0, 0.1))`,
                          border: `1px solid ${marketplaceData.isClaimEnabled ? 'rgba(0, 255, 136, 0.3)' : 'rgba(255, 107, 107, 0.3)'}`,
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">{marketplaceData.isClaimEnabled ? '✅' : '❌'}</div>
                            <h4 className="text-white mb-2">
                              {marketplaceData.isClaimEnabled ? 'ENABLED' : 'DISABLED'}
                            </h4>
                            <p className="text-white-50 mb-0">Claim Status</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={() => executeReadFunction({name: 'isClaimEnable', title: 'Claim Status', inputs: []})}
                              style={{
                                background: marketplaceData.isClaimEnabled ? 
                                  'rgba(0, 255, 136, 0.2)' : 'rgba(255, 107, 107, 0.2)',
                                border: marketplaceData.isClaimEnabled ? 
                                  '1px solid #00ff88' : '1px solid #ff6b6b',
                                color: marketplaceData.isClaimEnabled ? '#00ff88' : '#ff6b6b'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Queue Length and Contract Balance */}
                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, rgba(255, 170, 0, 0.15), rgba(255, 107, 107, 0.1))',
                          border: '1px solid rgba(255, 170, 0, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">📋</div>
                            <h4 className="text-white mb-2">{marketplaceData.queueLength || '0'}</h4>
                            <p className="text-white-50 mb-0">Queue Length</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={() => executeReadFunction({name: 'nftQueueLength', title: 'NFT Queue Length', inputs: []})}
                              style={{
                                background: 'rgba(255, 170, 0, 0.2)',
                                border: '1px solid #ffaa00',
                                color: '#ffaa00'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, rgba(0, 255, 136, 0.15), rgba(69, 183, 209, 0.1))',
                          border: '1px solid rgba(0, 255, 136, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">💰</div>
                            <h4 className="text-white mb-2">{marketplaceData.usdtBalance || '0.00'} USDT</h4>
                            <p className="text-white-50 mb-0">Contract Balance</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={loadMarketplaceData}
                              style={{
                                background: 'rgba(0, 255, 136, 0.2)',
                                border: '1px solid #00ff88',
                                color: '#00ff88'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, rgba(69, 183, 209, 0.15), rgba(107, 224, 215, 0.1))',
                          border: '1px solid rgba(69, 183, 209, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">💼</div>
                            <h4 className="text-white mb-2">{marketplaceData.userUsdtBalance || '0.00'} USDT</h4>
                            <p className="text-white-50 mb-0">Your Balance</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={loadMarketplaceData}
                              style={{
                                background: 'rgba(69, 183, 209, 0.2)',
                                border: '1px solid #45b7d1',
                                color: '#45b7d1'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Read Functions Tab */}
                  {activeTab === 'read' && (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h4 className="mb-0 text-info">👁️ Read Functions</h4>
                        <small className="text-white-50">
                          {marketplaceFunctions.readFunctions.length} functions available
                        </small>
                      </div>
                      {Object.entries(groupFunctionsByCategory(marketplaceFunctions.readFunctions)).map(([category, funcs]) => (
                        <div key={category} className="mb-5">
                          <h5 className="mb-3 text-white-50 d-flex align-items-center">
                            <span className="me-2">{category}</span>
                            <span className="badge bg-secondary rounded-pill">{funcs.length}</span>
                          </h5>
                          <div className="row">
                            {funcs.map(func => renderFunctionCard(func, false, false))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Write Functions Tab */}
                  {activeTab === 'write' && (
                    <div>
                      <h4 className="mb-4" style={{color: '#4ecdc4'}}>📝 Write Functions</h4>
                      {Object.entries(groupFunctionsByCategory(marketplaceFunctions.writeFunctions)).map(([category, funcs]) => (
                        <div key={category} className="mb-5">
                          <h5 className="mb-3 text-white-50">{category}</h5>
                          <div className="row">
                            {funcs.map(func => renderFunctionCard(func, true, false))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Admin Functions Tab */}
                  {activeTab === 'admin' && (
                    <div>
                      <h4 className="mb-4 text-danger">⚙️ Admin Functions</h4>
                      <div className="alert alert-warning">
                        <strong>⚠️ Warning:</strong> These functions are restricted to contract owner/admin only.
                      </div>
                      {Object.entries(groupFunctionsByCategory(marketplaceFunctions.adminFunctions)).map(([category, funcs]) => (
                        <div key={category} className="mb-5">
                          <h5 className="mb-3 text-white-50">{category}</h5>
                          <div className="row">
                            {funcs.map(func => renderFunctionCard(func, true, true))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}